import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { mergeDrugsThunk } from "../../../../redux/Thunk/Product/ProductThunks";
import apiAxios from "../../../../services/apiAxios";

const MergeDrugsModal = ({ isOpen, onClose, initialDrug = null, onSuccess }) => {
  const dispatch = useDispatch();
  const [sourceSearch, setSourceSearch] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [sourceResults, setSourceResults] = useState([]);
  const [targetResults, setTargetResults] = useState([]);
  const [recentDrugs, setRecentDrugs] = useState([]);
  const [isSearchingSource, setIsSearchingSource] = useState(false);
  const [isSearchingTarget, setIsSearchingTarget] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Initialize and load default drugs when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialDrug) {
        setSelectedSource(initialDrug);
        setSourceSearch("");
      } else {
        setSelectedSource(null);
        setSourceSearch("");
      }
      setSelectedTarget(null);
      setTargetSearch("");
      setError("");

      // Pre-fetch available drugs so the user has immediate options
      const fetchInitialDrugs = async () => {
        try {
          const res = await apiAxios.get("/api/product/drug/all?limit=12");
          setRecentDrugs(res?.data?.drugs || []);
        } catch (err) {
          console.error("Failed to fetch initial drugs:", err);
        }
      };
      fetchInitialDrugs();
    }
  }, [isOpen, initialDrug]);

  // Search source drugs
  useEffect(() => {
    if (!sourceSearch.trim()) {
      setSourceResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingSource(true);
      try {
        const res = await apiAxios.get(
          `/api/product/drug/all?search=${encodeURIComponent(sourceSearch.trim())}&limit=12`
        );
        setSourceResults(res?.data?.drugs || []);
      } catch (err) {
        console.error("Error searching source drugs:", err);
      } finally {
        setIsSearchingSource(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [sourceSearch]);

  // Search target drugs (exclude selected source)
  useEffect(() => {
    if (!targetSearch.trim()) {
      setTargetResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingTarget(true);
      try {
        const res = await apiAxios.get(
          `/api/product/drug/all?search=${encodeURIComponent(targetSearch.trim())}&limit=12`
        );
        const filtered = (res?.data?.drugs || []).filter(
          (d) => !selectedSource || d.id !== selectedSource.id
        );
        setTargetResults(filtered);
      } catch (err) {
        console.error("Error searching target drugs:", err);
      } finally {
        setIsSearchingTarget(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [targetSearch, selectedSource]);

  if (!isOpen) return null;

  const handleMerge = async (e) => {
    e.preventDefault();
    if (!selectedSource || !selectedTarget) {
      setError("Please select both a Source Drug and a Target Drug.");
      return;
    }
    if (selectedSource.id === selectedTarget.id) {
      setError("Source and Target drug cannot be identical.");
      return;
    }

    if (
      !window.confirm(
        `Confirm merging "${selectedSource.drugName}" into "${selectedTarget.drugName}"? This will update all assigned products and delete "${selectedSource.drugName}".`
      )
    ) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      await dispatch(
        mergeDrugsThunk({
          sourceDrugId: selectedSource.id,
          targetDrugId: selectedTarget.id,
        })
      ).unwrap();

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err?.error || err?.message || "Failed to merge drugs.");
    } finally {
      setLoading(false);
    }
  };

  // Filter available initial drugs for target picker (exclude source)
  const availableTargetRecents = recentDrugs.filter(
    (d) => !selectedSource || d.id !== selectedSource.id
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 transition-opacity">
      <div
        className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-lg flex flex-col max-h-[85vh] my-auto overflow-hidden transform transition-all animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50/50 to-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 leading-tight">
                Merge & Deduplicate Drugs
              </h3>
              <p className="text-xs text-gray-500 font-medium">
                Combine duplicates and update all product assignments automatically
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleMerge} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs font-semibold text-red-700">
                {error}
              </div>
            )}

            {/* Source Drug Picker */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">
                1. Source Drug (Duplicate to Remove) <span className="text-red-500">*</span>
              </label>

              {selectedSource ? (
                <div className="p-3 bg-purple-50/70 border border-purple-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs shrink-0">
                      #{selectedSource.id}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        {selectedSource.drugName}
                      </div>
                      <div className="text-[11px] text-purple-700 font-medium">
                        Assigned to {selectedSource.assignedProductCount || 0} product
                        {Number(selectedSource.assignedProductCount) === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSource(null);
                      setSourceSearch("");
                    }}
                    className="px-2.5 py-1 text-xs font-bold text-purple-700 hover:text-purple-900 hover:bg-purple-100 bg-white border border-purple-200 rounded-lg transition-colors cursor-pointer"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="relative">
                    <input
                      type="text"
                      value={sourceSearch}
                      onChange={(e) => setSourceSearch(e.target.value)}
                      placeholder="Type to search duplicate drug name..."
                      className="w-full pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:border-purple-500 focus:ring-2 focus:ring-purple-500/10"
                    />
                    <svg
                      className="w-4 h-4 text-gray-400 absolute left-3 top-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {isSearchingSource && (
                      <div className="absolute right-3 top-3">
                        <svg
                          className="animate-spin h-3.5 w-3.5 text-purple-600"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                      </div>
                    )}
                    {sourceSearch && !isSearchingSource && (
                      <button
                        type="button"
                        onClick={() => setSourceSearch("")}
                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 p-0.5"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* In-flow Results / Suggestions */}
                  <div className="bg-gray-50/70 border border-gray-200/80 rounded-xl max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {isSearchingSource ? (
                      <div className="p-3 text-center text-xs text-gray-400 font-medium">
                        Searching molecules...
                      </div>
                    ) : sourceSearch.trim() ? (
                      sourceResults.length > 0 ? (
                        sourceResults.map((d) => (
                          <div
                            key={d.id}
                            onClick={() => {
                              setSelectedSource(d);
                              setSourceSearch("");
                            }}
                            className="p-2.5 hover:bg-purple-50 flex items-center justify-between text-xs cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-400 font-bold">
                                #{d.id}
                              </span>
                              <span className="font-bold text-gray-900">{d.drugName}</span>
                            </div>
                            <span className="text-[10px] text-gray-500 font-mono">
                              {d.assignedProductCount || 0} products
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-xs text-gray-400">
                          No drugs found matching "{sourceSearch}".
                        </div>
                      )
                    ) : recentDrugs.length > 0 ? (
                      <div>
                        <div className="px-2.5 py-1.5 bg-gray-100/60 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          Available Drugs (Select or type above)
                        </div>
                        {recentDrugs.map((d) => (
                          <div
                            key={d.id}
                            onClick={() => {
                              setSelectedSource(d);
                              setSourceSearch("");
                            }}
                            className="p-2.5 hover:bg-purple-50 flex items-center justify-between text-xs cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-400 font-bold">
                                #{d.id}
                              </span>
                              <span className="font-bold text-gray-900">{d.drugName}</span>
                            </div>
                            <span className="text-[10px] text-gray-500 font-mono">
                              {d.assignedProductCount || 0} products
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-center text-xs text-gray-400">
                        Type in the box above to search drugs.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Merge Arrow Indicator */}
            <div className="flex items-center justify-center my-1 text-purple-600">
              <div className="w-8 h-8 rounded-full bg-purple-50 border border-purple-200 flex items-center justify-center shadow-2xs">
                <svg
                  className="w-4 h-4 transform rotate-90"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </div>
            </div>

            {/* Target Drug Picker */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">
                2. Target Canonical Drug (To Keep) <span className="text-red-500">*</span>
              </label>

              {selectedTarget ? (
                <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs shrink-0">
                      #{selectedTarget.id}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        {selectedTarget.drugName}
                      </div>
                      <div className="text-[11px] text-emerald-700 font-medium">
                        Assigned to {selectedTarget.assignedProductCount || 0} product
                        {Number(selectedTarget.assignedProductCount) === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTarget(null);
                      setTargetSearch("");
                    }}
                    className="px-2.5 py-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 bg-white border border-emerald-200 rounded-lg transition-colors cursor-pointer"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="relative">
                    <input
                      type="text"
                      value={targetSearch}
                      onChange={(e) => setTargetSearch(e.target.value)}
                      placeholder="Type to search canonical drug to keep..."
                      className="w-full pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
                    />
                    <svg
                      className="w-4 h-4 text-gray-400 absolute left-3 top-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {isSearchingTarget && (
                      <div className="absolute right-3 top-3">
                        <svg
                          className="animate-spin h-3.5 w-3.5 text-emerald-600"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                      </div>
                    )}
                    {targetSearch && !isSearchingTarget && (
                      <button
                        type="button"
                        onClick={() => setTargetSearch("")}
                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 p-0.5"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* In-flow Results / Suggestions */}
                  <div className="bg-gray-50/70 border border-gray-200/80 rounded-xl max-h-40 overflow-y-auto divide-y divide-gray-100">
                    {isSearchingTarget ? (
                      <div className="p-3 text-center text-xs text-gray-400 font-medium">
                        Searching molecules...
                      </div>
                    ) : targetSearch.trim() ? (
                      targetResults.length > 0 ? (
                        targetResults.map((d) => (
                          <div
                            key={d.id}
                            onClick={() => {
                              setSelectedTarget(d);
                              setTargetSearch("");
                            }}
                            className="p-2.5 hover:bg-emerald-50 flex items-center justify-between text-xs cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-400 font-bold">
                                #{d.id}
                              </span>
                              <span className="font-bold text-gray-900">{d.drugName}</span>
                            </div>
                            <span className="text-[10px] text-gray-500 font-mono">
                              {d.assignedProductCount || 0} products
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-xs text-gray-400">
                          No drugs found matching "{targetSearch}".
                        </div>
                      )
                    ) : availableTargetRecents.length > 0 ? (
                      <div>
                        <div className="px-2.5 py-1.5 bg-gray-100/60 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          Available Drugs (Select or type above)
                        </div>
                        {availableTargetRecents.map((d) => (
                          <div
                            key={d.id}
                            onClick={() => {
                              setSelectedTarget(d);
                              setTargetSearch("");
                            }}
                            className="p-2.5 hover:bg-emerald-50 flex items-center justify-between text-xs cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-400 font-bold">
                                #{d.id}
                              </span>
                              <span className="font-bold text-gray-900">{d.drugName}</span>
                            </div>
                            <span className="text-[10px] text-gray-500 font-mono">
                              {d.assignedProductCount || 0} products
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-center text-xs text-gray-400">
                        Type in the box above to search drugs.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Merge Explanation */}
            <div className="p-3.5 bg-gray-50 border border-gray-200/80 rounded-2xl text-xs text-gray-600 leading-relaxed">
              <span className="font-bold text-gray-900">What happens on merge:</span>
              <ul className="list-disc list-inside mt-1 space-y-0.5 text-[11px]">
                <li>All products referencing the source drug will be updated to the target drug.</li>
                <li>Any tags from the source drug will be transferred to the target drug.</li>
                <li>The source drug will be permanently deleted to prevent future duplication.</li>
              </ul>
            </div>
          </div>

          {/* Footer (Pinned at bottom) */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2.5 bg-gray-50/80 shrink-0">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedSource || !selectedTarget}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center gap-2 cursor-pointer"
            >
              {loading ? "Merging..." : "Merge Drugs"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MergeDrugsModal;
